/* eslint-disable react/display-name */
/* eslint-disable camelcase */

import React from 'react';
import dynamic from 'next/dynamic';
import Highlight from 'react-scale/components/ui/highlight';
import ExampleLoading from '../ExampleLoading';

const TaskResponseSemanticSegmentation = dynamic(
  () => import('../tasks/TaskResponseImageSemanticSegmentation'),
  {
    loading: () => <ExampleLoading />,
    ssr: false
  }
);

export const labelMapping = {
  background: {
    index: 1,
    color: '#ff0000'
  },
  road: {
    index: 2,
    color: '#ffff00'
  },
  vegetation: {
    index: 3,
    color: '#00ff00'
  },
  'lane marking': {
    index: 4,
    color: '#00ffff'
  },
  vehicle: [
    {
      index: 5,
      color: '#ffb43c'
    },
    {
      index: 6,
      color: '#64823c'
    },
    {
      index: 7,
      color: '#0000ff'
    },
    {
      index: 9,
      color: '#9617a1'
    },
    {
      index: 10,
      color: '#74f71f'
    },
    {
      index: 11,
      color: '#6faf78'
    },
    {
      index: 12,
      color: '#7ebf2b'
    },
    {
      index: 13,
      color: '#bd4a25'
    },
    {
      index: 14,
      color: '#557dce'
    },
    {
      index: 15,
      color: '#4e9e75'
    },
    {
      index: 16,
      color: '#a6323b'
    },
    {
      index: 17,
      color: '#751ca8'
    },
    {
      index: 18,
      color: '#04c2dd'
    },
    {
      index: 19,
      color: '#da3313'
    },
    {
      index: 20,
      color: '#cd51e2'
    },
    {
      index: 21,
      color: '#ae57eb'
    },
    {
      index: 22,
      color: '#26e73b'
    },
    {
      index: 23,
      color: '#cca267'
    },
    {
      index: 24,
      color: '#a34868'
    },
    {
      index: 25,
      color: '#0b5592'
    }
  ],
  pedestrian: [
    {
      index: 8,
      color: '#ff00ff'
    }
  ]
};

export const instruction = `Segment by vehicles, pedestrians,
bikes, roads, road markings, background and obstacles
in each image.`;

export const responseCode = `{
  "response" : {
        "instances" : {},
        "labelMapping" : {
            "Background" : {
                "color" : "#ff00ff",
                "index" : 8
            },
            "Pedestrian" : {
                "color" : "#0000ff",
                "index" : 7
            },
            "Bike" : {
                "color" : "#64823c",
                "index" : 6
            },
            "Vehicle" : {
                "color" : "#ffb43c",
                "index" : 5
            },
            "Debris (can hit)" : {
                "color" : "#00ffff",
                "index" : 4
            },
            "Obstacle (can't hit)" : {
                "color" : "#00ff00",
                "index" : 3
            },
            "Road Marking" : {
                "color" : "#ffff00",
                "index" : 2
            },
            "Road" : {
                "color" : "#ff0000",
                "index" : 1
            }
        },
        "annotations" : {
            "combined" : {
                "indexedImage" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/ba9a9d70-8119-11e7-ac39-7d56f40a5f60",
                "image" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b7573241-8119-11e7-ac39-7d56f40a5f60"
            },
            "labeled" : {
                "Background" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b7573240-8119-11e7-ac39-7d56f40a5f60",
                "Pedestrian" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b7570b30-8119-11e7-ac39-7d56f40a5f60",
                "Bike" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b756e421-8119-11e7-ac39-7d56f40a5f60",
                "Vehicle" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b756e420-8119-11e7-ac39-7d56f40a5f60",
                "Debris (can hit)" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b756bd11-8119-11e7-ac39-7d56f40a5f60",
                "Obstacle (can't hit)" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b756bd10-8119-11e7-ac39-7d56f40a5f60",
                "Road Marking" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b7566ef0-8119-11e7-ac39-7d56f40a5f60",
                "Road" : "https://scaleapi-attachments.s3-ap-northeast-1.amazonaws.com/b755d2b0-8119-11e7-ac39-7d56f40a5f60"
            },
            "unlabeled" : null
        }
    },
    "task_id": "598e0da3e8068e06002d9407"
}`;

export const code = `client.createSegmentannotationTask({
  instruction: '${instruction}',
  attachment: 'http://i.imgur.com/XOJbalC.jpg',
  labels: ['vehicle', 'background', 'road', 'vegetation',
  'lane marking']
}, (err, task) => {
    // do something with task
});`;

export const response = (
  <div className="data-collection">
    <Highlight className="javascript" key="highlight-json">
      {responseCode}
    </Highlight>
    <style jsx>{`
      @media only screen and (min-width: 768px) {
        .data-collection {
          height: 210px;
          overflow: scroll;
        }
      }
    `}</style>
  </div>
);

export const example = () => (
  <TaskResponseSemanticSegmentation
    src="/static/img/examples/ss-raw-self-driving-cars.jpg"
    responsive
    rounded
  />
);

export const result = (
  <TaskResponseSemanticSegmentation
    src="/static/img/examples/ss-raw-self-driving-cars.jpg"
    combined="/static/img/examples/ss-response-self-driving-cars.png"
    labelMapping={labelMapping}
  />
);
